import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { PAPER_BASE } from './config';
import PaperSEO from '../../components/paper/PaperSEO';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL;

const CATEGORY_LABEL = {
  FESTIVAL: '축제',
  EVENT: '공연',
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}.${m}.${dd}`;
};

export default function PaperBlogDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    axios.get(`${API}/api/paperBlog/${id}`)
      .then(res => {
        setBlog(res.data.blog);
      })
      .catch(err => {
        if (err.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="bg-[#0a0a0a] min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-violet-400/30 border-t-violet-400 rounded-full animate-spin" />
      </div>
    );
  }

  if (notFound || !blog) {
    return (
      <div className="bg-[#0a0a0a] min-h-screen flex flex-col items-center justify-center gap-4">
        <p className="text-white/30 text-lg">존재하지 않는 글입니다</p>
        <button
          onClick={() => navigate(`${PAPER_BASE}/blog`)}
          className="text-violet-400 hover:text-violet-300 text-sm transition-colors"
        >
          목록으로 돌아가기
        </button>
      </div>
    );
  }

  const work = blog.work;

  return (
    <div className="bg-[#0a0a0a] min-h-screen">
      <PaperSEO
        title={blog.title}
        description={blog.summary || ''}
        path={`/blog/${blog.id}`}
        image={blog.thumbnailUrl}
        type="article"
      />

      <article className="max-w-3xl mx-auto px-4 sm:px-6 pt-10 pb-20">
        {/* Header */}
        <header className="mb-10">
          {work && (
            <Link
              to={`${PAPER_BASE}/work/${work.id}`}
              className="inline-block bg-violet-500/15 text-violet-400 text-[11px] font-semibold px-3 py-1 rounded-full mb-4 hover:bg-violet-500/25 transition-colors"
            >
              {CATEGORY_LABEL[work.category] || work.category} &middot; {work.title}
            </Link>
          )}

          <h1 className="text-white text-2xl sm:text-3xl font-bold leading-tight mb-4">
            {blog.title}
          </h1>

          <div className="flex items-center gap-3 text-white/30 text-sm">
            {blog.publishedAt && <span>{formatDate(blog.publishedAt)}</span>}
          </div>
        </header>

        {/* Thumbnail */}
        {blog.thumbnailUrl && (
          <div className="rounded-2xl overflow-hidden mb-10 border border-white/5">
            <img
              src={blog.thumbnailUrl}
              alt={blog.title}
              className="w-full aspect-video object-cover"
            />
          </div>
        )}

        {/* Content */}
        <div
          className="prose prose-invert prose-violet max-w-none
            prose-headings:text-white prose-headings:font-semibold
            prose-p:text-white/70 prose-p:leading-relaxed
            prose-a:text-violet-400 prose-a:no-underline hover:prose-a:text-violet-300
            prose-strong:text-white/90
            prose-img:rounded-xl prose-img:border prose-img:border-white/5
            prose-blockquote:border-violet-400/30 prose-blockquote:text-white/50"
          dangerouslySetInnerHTML={{ __html: blog.content }}
        />

        {/* Back Button */}
        <div className="mt-16 pt-8 border-t border-white/5">
          <button
            onClick={() => navigate(`${PAPER_BASE}/blog`)}
            className="flex items-center gap-2 text-white/40 hover:text-violet-400 transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            목록으로 돌아가기
          </button>
        </div>
      </article>
    </div>
  );
}
